import { clsx } from 'clsx';
import { forwardRef, useCallback } from 'react';
import { spacing } from '../../styles/spacing.css';
import { ellipsis, floatingLabel } from '../../styles/utilities.css';
import { CURSOR_POSITION, label as inputLabel } from '../Input/Input.css';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import * as styles from './Trigger.css';

interface TriggerValueProps {
  readonly placeholder?: string;
  readonly value?: string;
}

const TriggerValue: React.FC<TriggerValueProps> = ({ placeholder, value }) => {
  const hasValue = Boolean(value);
  return (
    <Layout minWidth="176px" position="relative" textAlign="left" width="100%">
      {placeholder != null && (
        <Text
          as="span"
          className={clsx(floatingLabel, ellipsis)}
          color="$textTertiary"
          left={`${CURSOR_POSITION}px`}
          position="absolute"
          top={hasValue ? `-${spacing.s08}` : 0}
          type={hasValue ? 'bodyTiny' : 'bodyMedium'}
          width={`calc(100% - ${spacing.s56})`}
        >
          {placeholder}
        </Text>
      )}
      <Layout className={styles.selectedValue({ hasValue })} top={placeholder != null ? spacing.s08 : 0}>
        <Text className={styles.triggerContent} display="inline-block" type="bodyMedium">
          {value}
        </Text>
      </Layout>
    </Layout>
  );
};

export interface TriggerProps {
  readonly as?: React.ElementType;
  readonly label?: string;
  readonly rightIcon?: React.ReactNode;
  readonly leftIcon?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly isActive?: boolean;
  readonly hasError?: boolean;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly value?: string;
  readonly onClick?: () => void;
  readonly setTriggerWidth?: React.Dispatch<React.SetStateAction<number>>;
}

export const Trigger = forwardRef<HTMLElement, TriggerProps>(
  (
    {
      label,
      rightIcon,
      leftIcon,
      children,
      isActive,
      hasError = false,
      value,
      placeholder,
      disabled,
      onClick,
      as,
      setTriggerWidth,
    },
    ref,
  ) => {
    const Component = as ?? 'button';
    const componentProps = {
      disabled,
      ...(Component === 'button' && { type: 'button' }),
    };

    const refCallback = useCallback(
      (e: HTMLDivElement | null) => {
        if (typeof ref === 'function') {
          ref(e);
        }

        setTriggerWidth?.((e?.offsetWidth ?? 0) + styles.TRIGGER_BORDER_WIDTH * 2);
      },
      [ref, setTriggerWidth],
    );

    return (
      <Component
        aria-label={placeholder}
        className={styles.selectTrigger({ hasError, disabled })}
        data-testid="select-trigger"
        onClick={onClick}
        ref={refCallback}
        {...componentProps}
      >
        {leftIcon != null && leftIcon}
        {label != null && (
          <span
            className={clsx(
              inputLabel({ showIcon: leftIcon != null }),
              {
                active: isActive,
              },
              styles.baseLabel({ hasError, disabled }),
            )}
          >
            {label}
          </span>
        )}
        {children ?? <TriggerValue placeholder={placeholder} value={value} />}
        {rightIcon != null && rightIcon}
      </Component>
    );
  },
);
